import { Button } from "@progress/kendo-react-buttons";
import { Badge, BadgeContainer } from "@progress/kendo-react-indicators";
import { Switch } from "@progress/kendo-react-inputs";
import { Label } from "@progress/kendo-react-labels";
import { Popover, PopoverActionsBar } from "@progress/kendo-react-tooltip";
import { useEffect, useRef, useState } from "react";
import { envVariables } from "../../Common/Store/CommonSignals";
import { isTemplatePersisted, templateData } from "../../Template/NewTemplateSignals";
import authorizeIP from "./AuthorizeIp";
import getUserIP from "./GetUserIp";
import validateIPWithFirewall from "./ValidateIpWithFirewall";

const SyncData = () => {
  const [show, setShow] = useState(false);
  const [sqlCred, setSqlCred] = useState(null);
  const [ipValidationStatus, setIpValidationStatus] = useState(null);
  const anchor = useRef(null);

  const syncOnChange = (e) => {
    templateData.value = {
      ...templateData.value,
      properties: { ...templateData.value.properties, TemplateSync: e.value },
    };
  };

  //#region Report Credentials
  useEffect(() => {
    validateIP();
    fetchSqlCred(); // Call validateIP when the component mounts
  }, []);

  const handleAuthorizeIP = async () => {
    const ip = await getUserIP();

    // Await the completion of authorizeIP before running validateIP
    const authResult = await authorizeIP(
      ip,
      templateData.value?.id,
      validateIP,
      envVariables.value
    );

    // If authResult is true or needs to be revalidated, call validateIP
    if (authResult) {
      validateIP();
    }
  };
  const validateIP = async () => {
    const ip = await getUserIP();
    const result = await validateIPWithFirewall(ip, envVariables.value);
    setIpValidationStatus(!!result);
  };

  const fetchSqlCred = async () => {
    const baseURL = envVariables.value.REACT_APP_ATLAS_AZFUNCTION || "";
    const apiKey = envVariables.value.REACT_APP_ATLAS_AZFKEY_GETSQLUSER || "";
    const url = `${baseURL}credentials/${templateData.value?.id}${apiKey}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      });

      if (response.status !== 200) {
        // Handle unexpected status codes here
        setSqlCred(null);
        return;
      }

      const result = await response.json();
      setSqlCred(result);
    } catch (error) {
      console.error("An error occurred while fetching data:", error);
      setSqlCred(null);
    }
  };

  const ShowCredentials = () => {
    setShow(!show);
    if (!show) fetchSqlCred();
  };

  //#endregion

  return (
    <div className="align-columns">
      <Label className="template-sync-label">
        <strong>Enable Data Sync for Reports:</strong>
      </Label>
      <Switch
        className="template-sync-switch"
        checked={templateData.value?.properties?.["TemplateSync"]}
        name="TemplateSync"
        offLabel="I"
        onLabel="O"
        onChange={(e) => syncOnChange(e)}
        disabled={
          !templateData.value?.properties?.AllowFieldNameEdit &&
          templateData.value?.properties?.["TemplateSync"]
        }
      />
      {isTemplatePersisted.value && (
        <BadgeContainer className="k-mt-5">
          <Button
            className="k-ml-2.5"
            onClick={ShowCredentials}
            ref={(btn) => (anchor.current = btn?.element)}
            disabled={show}
          >
            Report Credentials
          </Button>
          <Badge
            size="small"
            align={{ vertical: "top", horizontal: "end" }}
            className="!k-w-4.5 !k-h-4.5"
            themeColor={ipValidationStatus ? "success" : "error"}
            cutoutBorder={true}
          >
            <span className={`k-icon k-i-${ipValidationStatus ? "check" : "minus"}`} />
          </Badge>
          <Badge
            size="small"
            align={{ vertical: "top", horizontal: "end" }}
            className="k-mr-6 !k-w-4.5 !k-h-4.5"
            themeColor={sqlCred ? "success" : "error"}
            cutoutBorder={true}
          >
            <span className="k-icon k-i-check" />
          </Badge>
        </BadgeContainer>
      )}
      <Popover
        show={show}
        anchor={anchor.current}
        title={"Report Credentials"}
        position="bottom"
      >
        <a
          target="blank"
          className="report-credential-link"
          href="https://compassminerals0-my.sharepoint.com/:v:/g/personal/ribeirov_compassminerals_com/EfZYDzcVJDdFmuhd-N2WfgYBuTD-fbFK0kgYJiXmm1iaMA?nav=eyJyZWZlcnJhbEluZm8iOnsicmVmZXJyYWxBcHAiOiJTdHJlYW1XZWJBcHAiLCJyZWZlcnJhbFZpZXciOiJTaGFyZURpYWxvZyIsInJlZmVycmFsQXBwUGxhdGZvcm0iOiJXZWIiLCJyZWZlcnJhbE1vZGUiOiJ2aWV3In19&e=T6gFIT"
        >
          How to Connect to Power BI
        </a>

        <div style={{ width: "500px" }}>
          {show && sqlCred ? (
            <div className="k-coloreditor k-w-full">
              <div className="!k-font-bold !k-p-2 !k-mb-1">
                Server Url:{" "}
                <span className="k-font-normal">{envVariables.value.REACT_APP_SQL_SERVER_URL}</span>
              </div>
              <div className="!k-font-bold !k-p-2 !k-mb-1">
                Database:{" "}
                <span className="k-font-normal">
                  {envVariables.value.REACT_APP_SQL_SERVER_DATABASE}
                </span>
              </div>
              <div className="!k-font-bold !k-p-2 !k-mb-1">
                Username: <span className="k-font-normal">{sqlCred.username}</span>
              </div>
              <div className="!k-font-bold !k-p-2 !k-mb-1">
                Password: <span className="k-font-normal">{sqlCred.password}</span>
              </div>
            </div>
          ) : (
            <div className="k-text-error">- This Template Doesn`t have Report Credentials</div>
          )}
          {ipValidationStatus === true ? (
            <div className="k-text-success k-mt-2">- Your IP is already authorized</div>
          ) : (
            <div className="k-text-error">- You need to Authorize your IP</div>
          )}
        </div>
        <PopoverActionsBar>
          <div className="k-d-flex k-justify-content-between k-w-full">
            {ipValidationStatus === false && <Button onClick={handleAuthorizeIP}>Authorize</Button>}
            <Button onClick={ShowCredentials}>Close</Button>
          </div>
        </PopoverActionsBar>
      </Popover>
    </div>
  );
};

export default SyncData;
